import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Truck, ShoppingCart, Megaphone, Gift, TrendingUp, Headphones, RotateCcw, CheckCircle, MessageSquare, Smartphone, Megaphone as RcsIcon, PhoneCall } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Helmet } from "react-helmet-async";
import FlatIcon from "@/components/site/FlatIcon";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import CTASection from "@/components/site/CTASection";
import Seo from "@/components/Seo";


const useCases = [
  {
    title: "Order Confirmations",
    description: "Build trust with instant notifications the moment a purchase goes through.",
    Icon: ShoppingBag,
    color: "text-amber",
    tags: ["SMS", "RCS"],
  },
  {
    title: "Shipping & Delivery Updates",
    description: "Let customers know where their package is with real-time delivery alerts.",
    Icon: Truck,
    color: "text-sky",
    tags: ["SMS", "RCS"],
  },
  {
    title: "Cart Abandonment Reminders",
    description: "Recover abandoned carts with friendly, timely reminders.",
    Icon: ShoppingCart,
    color: "text-lime",
    tags: ["SMS"],
  },
  {
    title: "Exclusive Promotions",
    description: "Deliver limited-time offers and discounts in seconds to prompt quick action.",
    Icon: Megaphone,
    color: "text-violet",
    tags: ["SMS", "RCS"],
  },
  {
    title: "Loyalty Program Updates",
    description: "Share points balances or VIP offers to keep engagement high.",
    Icon: Gift,
    color: "text-pink",
    tags: ["SMS"],
  },
];

const enhancedBenefits = [
  {
    title: "Higher Campaign Engagement",
    description: "Direct, personalized messages reach customers where they already check, driving opens, clicks, and conversions faster than email alone.",
    Icon: TrendingUp,
    color: "text-amber",
  },
  {
    title: "Fewer Support Tickets",
    description: "Proactive delivery updates and order notifications answer customer questions before they ever need to ask.",
    Icon: Headphones,
    color: "text-sky",
  },
  {
    title: "Recover More Abandoned Carts",
    description: "Timely SMS reminders nudge hesitant shoppers back to complete their purchase.",
    Icon: RotateCcw,
    color: "text-lime",
  },
];

const supportedChannels = [
  { title: "SMS", description: "Reach customers reliably for promotions and order updates.", Icon: MessageSquare, gradient: "icon-tile-teal" },
  { title: "RCS", description: "Deliver interactive campaign and order content.", Icon: RcsIcon, gradient: "icon-tile-indigo" },
  { title: "Voice", description: "Reach customers with automated voice calls for urgent updates.", Icon: PhoneCall, gradient: "icon-tile-blue" },
];

const faqs = [
  { question: "What is ecommerce SMS marketing?", answer: "Ecommerce SMS marketing lets online retailers send personalized texts directly to customers, including promotions, order updates, or loyalty rewards, to drive engagement and maintain a strong customer relationship throughout the shopping journey." },
  { question: "What kind of notifications can I send?", answer: "You can send order confirmations, shipping alerts, cart reminders, product launches, and exclusive discounts, keeping customers informed and encouraging repeat purchases." },
  { question: "Is SMS marketing for ecommerce legal?", answer: "Yes, provided brands follow compliance guidelines, obtaining customer consent, offering opt-out options, and respecting data privacy requirements." },
  { question: "Can Yootelco help reduce cart abandonment?", answer: "Yes, automated cart abandonment reminders sent via SMS are one of the most effective ways to recover hesitant shoppers." },
  { question: "What channels work best for ecommerce messaging?", answer: "SMS offers the broadest reach, while RCS supports richer, branded messaging for promotions and order updates." },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
};

const channelIcons = [
  { label: "SMS", Icon: MessageSquare },
  { label: "RCS", Icon: RcsIcon },
];

const IndustriesRetailEcommerce = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <main className="industries-page min-h-screen bg-white">
      <Seo
        title="Retail & Ecommerce SMS Marketing | Yootelco"
        description="Boost sales with SMS marketing for retail and ecommerce. Send order confirmations, shipping updates, cart reminders, and promotions via SMS and RCS."
        path="/industries/retail-ecommerce"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>
      <SiteHeader />

      {/* Hero */}
      <section className="pb-16 pt-[120px] md:pb-20 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container">
          <div className="mx-auto max-w-[800px] text-center">
            <span className="inline-block rounded-full bg-primary/15 px-4 py-1.5 text-[13px] font-bold uppercase tracking-wider text-foreground">
              Connect With Shoppers Directly
            </span>
            <h1 className="mt-5 font-display text-[44px] font-bold leading-[1.05] tracking-tight text-foreground md:text-[60px] [text-wrap:balance]">
              Messaging for Retail & Ecommerce
            </h1>
            <p className="mx-auto mt-[18px] max-w-[700px] text-[15px] font-medium leading-[1.55] text-muted-foreground md:text-[16px] [text-wrap:balance]">
              Drive sales, boost engagement, and deliver personalized experiences that keep customers coming back. Yootelco helps online stores keep customers informed and engaged from checkout to delivery.
            </p>
            <Link
              to="/contact"
              className="mt-[28px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
            >
              Contact Sales
            </Link>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-5">
              {channelIcons.map((channel) => (
                <div key={channel.label} className="flex items-center gap-2 text-[13px] font-semibold text-muted-foreground">
                  <channel.Icon className="h-4 w-4 text-primary" strokeWidth={2.2} />
                  {channel.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Retail & Ecommerce Choose Yootelco */}
      <section className="section-shell">
        <div className="mx-auto max-w-[760px] text-center">
          <h2 className="section-title">Why Retail & Ecommerce Choose Yootelco</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            With more than 18 years of experience supporting retail and ecommerce businesses, we help brands connect with customers through high-performing communication and marketing solutions. Whether you're looking to increase sales, recover abandoned carts, promote seasonal campaigns, or improve customer engagement, our team delivers tailored strategies that drive measurable results.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            From customer acquisition and retention to personalized messaging and omnichannel communication, we help ecommerce businesses optimize every stage of the customer journey.
          </p>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Use Cases of Retail & Ecommerce Messaging</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {useCases.map((item) => {
            const Icon = item.Icon;
            return (
              <article
                key={item.title}
                className="flex flex-col rounded-[18px] border border-border/60 bg-card px-6 py-7 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <FlatIcon Icon={Icon} color={item.color} className="mx-auto block w-fit" />
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
                <p className="mt-[10px] flex-1 text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                  {item.tags.map((tag) => (
                    <span key={tag} className="rounded-full bg-secondary px-2.5 py-1 text-[11px] font-semibold text-foreground/80">
                      {tag}
                    </span>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Enhanced Benefits */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Enhanced Benefits for Retail & Ecommerce</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {enhancedBenefits.map((item) => {
            const Icon = item.Icon;
            return (
              <article
                key={item.title}
                className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <FlatIcon Icon={Icon} color={item.color} />
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
                <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* Stay Connected and Compliant */}
      <section className="section-shell">
        <div className="mx-auto max-w-[760px] text-center">
          <h2 className="section-title">Stay Connected and Compliant</h2>
          <div className="mt-[24px] mx-auto max-w-[640px] text-left">
            {[
              "Direct connections to mobile network operators",
              "Multi-channel delivery across SMS and RCS",
              "Built for high-volume promotional and transactional sends",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 py-3">
                <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary/10 text-primary">
                  <CheckCircle className="h-3.5 w-3.5" strokeWidth={2.5} />
                </span>
                <p className="text-[15px] leading-[1.55] text-muted-foreground">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supported Channels */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Supported Channels</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {supportedChannels.map((channel) => {
            const Icon = channel.Icon;
            return (
              <article
                key={channel.title}
                className="flex flex-col items-center rounded-[18px] border border-border/60 bg-secondary/40 px-6 py-8 text-center transition-all duration-300 hover:-translate-y-1"
              >
                <span className={`flex h-12 w-12 items-center justify-center rounded-[12px] ${channel.gradient}`}>
                  <Icon className="h-6 w-6" strokeWidth={2.2} />
                </span>
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{channel.title}</h3>
                <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{channel.description}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* CTA Banner */}
            <CTASection
        title="Talk to Sales"
        description="Tell us about your store's messaging needs, we'll show you how Yootelco can support your campaigns and order updates."
        useCase="Retail & Ecommerce"
      />

      {/* FAQ */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Frequently Asked Questions</h2>
        </div>
        <div className="mx-auto mt-10 max-w-[1120px]">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((item, index) => (
              <AccordionItem key={item.question} value={`item-${index}`} className="border-b border-border">
                <AccordionTrigger className="py-6 text-left text-lg font-bold hover:no-underline md:text-xl">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 pr-12 text-base leading-7 text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-shell">
        <div className="mx-auto max-w-[800px] rounded-[36px] border border-border/70 bg-gradient-to-br from-secondary/60 to-card px-6 py-14 text-center shadow-soft md:px-10 md:py-20">
          <h2 className="section-title">Ready to Engage Your Shoppers?</h2>
          <p className="mx-auto mt-5 max-w-xl text-lg text-muted-foreground">
            Talk to our team about SMS campaigns and order notifications for your store.
          </p>
          <Link to="/contact" className="mt-8 inline-flex rounded-xl bg-primary px-8 py-4 text-base font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift">
            Contact Sales
          </Link>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
};

export default IndustriesRetailEcommerce;
